<template>
  <base-card>
    <div class="tw-flex tw-flex-col">
      <div class="tw-text-center tw-mb-4">
        <h4 class="tw-text-gray-600 tw-text-base tw-font-normal">
          {{ chartTitle }}
        </h4>
      </div>

      <div class="chart-container tw-self-center tw-w-full">
        <inline-error id="daily-projection-error" :title="errorTitle" :message="errorMessage" :error="error" />

        <!-- Loading skeleton -->
        <div v-if="isLoading" class="tw-flex tw-flex-col tw-gap-4 tw-p-4">
          <div class="tw-flex tw-gap-4">
            <div class="tw-flex tw-flex-col tw-justify-between tw-items-end" style="height: 20rem; width: 60px;">
              <base-skeleton-loader class="tw-h-4 tw-w-12" />
              <base-skeleton-loader class="tw-h-4 tw-w-12" />
              <base-skeleton-loader class="tw-h-4 tw-w-12" />
              <base-skeleton-loader class="tw-h-4 tw-w-12" />
              <base-skeleton-loader class="tw-h-4 tw-w-12" />
            </div>

            <div class="tw-flex-1 tw-relative" style="height: 20rem;">
              <div class="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-justify-between">
                <div class="tw-w-full tw-h-px tw-bg-gray-200"></div>
                <div class="tw-w-full tw-h-px tw-bg-gray-200"></div>
                <div class="tw-w-full tw-h-px tw-bg-gray-200"></div>
                <div class="tw-w-full tw-h-px tw-bg-gray-200"></div>
                <div class="tw-w-full tw-h-px tw-bg-gray-200"></div>
              </div>

              <div class="tw-absolute tw-inset-0 tw-flex tw-items-end tw-justify-around tw-px-2">
                <div v-for="i in 12" :key="i" class="tw-flex tw-flex-col tw-items-center tw-gap-1">
                  <div class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-gray-300"></div>
                  <div class="tw-w-px tw-bg-gray-300" :style="{ height: 10 + i * 5 + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="tw-flex tw-gap-2 tw-justify-around tw-ml-16">
            <base-skeleton-loader v-for="i in 10" :key="i" class="tw-h-3 tw-w-12" />
          </div>
        </div>

        <!-- Chart -->
        <line-chart v-else v-show="errorMessage == ''" :chart-data="chartData" :options="chartOptions" style="height: 20rem" />
      </div>
    </div>
  </base-card>
</template>

<script>
import LineChart from "@/components/charts/LineChart";
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";

const formatter = new Intl.NumberFormat(window.navigator.language, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default {
  name: "ProviderDailyRevenueWithProjection",
  components: { LineChart, InlineError },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      isLoading: false,
      windowWidth: window.innerWidth,
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    isMobile() {
      return this.windowWidth <= 768;
    },
    chartTitle() {
      if (this.data && this.data.length > 0) {
        const firstDay = this.data[0];
        const lastDay = this.data[this.data.length - 1];

        return `Revenue Trend (${firstDay.monthName} ${firstDay.year} - ${lastDay.monthName} ${lastDay.year})`;
      }

      return "Revenue Trend";
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                min: 0,
                callback: (label) => {
                  return formatter.format(label);
                },
              },
              gridLines: {
                color: "rgba(0, 0, 0, 0.1)",
                drawBorder: true,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: true,
                color: "rgba(0, 0, 0, 0.05)",
                drawBorder: true,
              },
              ticks: {
                autoSkip: false,
                maxRotation: 45,
                minRotation: 45,
                callback: (value, index) => {
                  if (this.data && this.data[index]) {
                    const day = this.data[index];
                    if (day.dayNum === 1) {
                      return day.monthName;
                    }
                  }
                  return "";
                },
              },
              offset: false,
            },
          ],
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 15,
          },
        },
        elements: {
          line: {
            tension: 0,
            borderWidth: 2,
          },
          point: {
            radius: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value > 0 ? 3 : 0;
            },
            hitRadius: 10,
            hoverRadius: 5,
          },
        },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleFontSize: 12,
          bodyFontSize: 12,
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems && tooltipItems.length > 0) {
                const index = tooltipItems[0].index;
                if (this.data && this.data[index]) {
                  const day = this.data[index];
                  return `${day.monthName} ${day.dayNum}, ${day.year}`;
                }
              }
              return "";
            },
            label: (tooltipItem, data) => {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || "";
              return `${datasetLabel}: ${formatter.format(tooltipItem.yLabel)}`;
            },
          },
        },
      };
    },
    chartData() {
      if (!this.data || !this.data.length) {
        return null;
      }

      const labels = this.data.map((item) => item.dayLabel);
      const chargedData = this.data.map((item) => item.chargedRevenueInCents / 100);
      const predictedData = this.data.map((item) => item.predictedRevenueInCents / 100);

      return {
        labels,
        datasets: [
          {
            label: "Charged",
            data: chargedData,
            borderColor: this.colors.charged.borderColor,
            backgroundColor: this.colors.charged.backgroundColor,
            pointBackgroundColor: this.colors.charged.pointColor,
            pointBorderColor: this.colors.charged.pointColor,
            pointHoverBackgroundColor: this.colors.charged.pointColor,
            pointHoverBorderColor: this.colors.charged.pointColor,
            borderWidth: 2,
            fill: false,
            spanGaps: true,
          },
          {
            label: "Predicted",
            data: predictedData,
            borderColor: this.colors.predicted.borderColor,
            backgroundColor: this.colors.predicted.backgroundColor,
            pointBackgroundColor: this.colors.predicted.pointColor,
            pointBorderColor: this.colors.predicted.pointColor,
            pointHoverBackgroundColor: this.colors.predicted.pointColor,
            pointHoverBorderColor: this.colors.predicted.pointColor,
            borderWidth: 2,
            fill: false,
            spanGaps: true,
          },
        ],
      };
    },
    colors() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return {
            charged: {
              borderColor: "rgba(85, 185, 72, 0.7)",
              backgroundColor: "rgba(85, 185, 72, 0.7)",
              pointColor: "rgba(85, 185, 72, 0.7)",
            },
            predicted: {
              borderColor: "rgba(85, 185, 72, 0.3)",
              backgroundColor: "rgba(85, 185, 72, 0.3)",
              pointColor: "rgba(85, 185, 72, 0.3)",
            },
          };
      }

      // default (pink theme)
      return {
        charged: {
          borderColor: "#ff0090",
          backgroundColor: "transparent",
          pointColor: "#ff0090",
        },
        predicted: {
          borderColor: "rgba(255, 0, 144, 0.35)",
          backgroundColor: "transparent",
          pointColor: "rgba(255, 0, 144, 0.35)",
        },
      };
    },
  },
  created() {
    this.loadDailyRevenueData();
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    loadDailyRevenueData() {
      this.isLoading = true;
      this.error = null;
      this.errorMessage = "";

      const url = `/businesses/${this.businessId}/dashboard/provider/daily-revenue-with-projection`;

      this.$axios
        .get(url)
        .then((res) => {
          this.data = res.data.results || [];
        })
        .catch((err) => {
          this.errorTitle = "Failed to load revenue data";
          this.errorMessage = "Failed to load revenue data";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>
